import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fetchAgentById, updateAgentById } from "../../services/agentService";
import { AgentContext } from "../../context/AgentContext";
import { 
  TrashIcon, 
  DocumentArrowUpIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ArrowPathIcon,
  EyeIcon,
  CloudArrowUpIcon
} from "@heroicons/react/24/outline";

const API_BASE = process.env.REACT_APP_API_BASE;

const PERSONALITY_OPTS = [
    { label: "Gen Z", desc: "Santai, gaul, menggunakan slang." },
    { label: "Professional", desc: "Formal, sopan, to-the-point." },
    { label: "Empathetic", desc: "Penuh pengertian, sabar." },
    { label: "Formal", desc: "Sangat baku, kaku, sesuai EYD." }
];

export default function EditAgent() {
  const { agentId } = useParams();
  const { refreshAgents } = useContext(AgentContext);
  
  const [formData, setFormData] = useState({
    agentName: "", agentPrompt: "", agentPersonality: "", businessDescription: "",
  });
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: "", message: "" });
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedHistory, setUploadedHistory] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [failedFiles, setFailedFiles] = useState([]);
  
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // --- PROTEKSI LEAVE PAGE ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploading]);

  // --- FETCH DATA ---
  const fetchUploadedFiles = useCallback(async () => {
    try {
        const res = await axios.get(`${API_BASE}/api/knowledge/${agentId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        setUploadedHistory(res.data);
    } catch (error) { console.error("History Error", error); }
  }, [agentId]);

  useEffect(() => {
    fetchAgentById(agentId).then((data) => {
        setFormData({
          agentName: data.agentName || "",
          agentPrompt: data.agentPrompt || "",
          agentPersonality: data.agentPersonality || "Professional",
          businessDescription: data.businessDescription || "",
        });
        setLoading(false);
    }).catch(console.error);
    fetchUploadedFiles();
  }, [agentId, fetchUploadedFiles]);

  const handleDeleteHistory = async (fileId) => {
      if(!window.confirm("Hapus file ini permanen?")) return;
      try {
          await axios.delete(`${API_BASE}/api/knowledge/${fileId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          });
          fetchUploadedFiles();
      } catch (error) { alert("Gagal menghapus file"); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- DRAG & DROP ---
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => { setIsDragging(false); };
  const onDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    if (e.dataTransfer.files) validateAndAddFiles(Array.from(e.dataTransfer.files));
  };
  const onFileSelect = (e) => { validateAndAddFiles(Array.from(e.target.files)); };
  
  const validateAndAddFiles = (files) => {
    const validTypes = ['application/pdf', 'text/plain'];
    const validFiles = files.filter(file => validTypes.includes(file.type));
    if (validFiles.length !== files.length) alert("Hanya file PDF dan TXT yang diperbolehkan.");
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };
  
  // eslint-disable-next-line no-unused-vars
  const removeFile = useCallback((index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index)), []);

  // --- UPLOAD LOGIC ---
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setFailedFiles([]); 
    
    let successCount = 0;
    let currentFailures = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadStatus({ 
        type: 'info', 
        message: `⏳ Mengupload (${i + 1}/${selectedFiles.length}): ${file.name}` 
      });

      const uploadPayload = new FormData();
      uploadPayload.append("document", file);
      uploadPayload.append("agentId", agentId);

      try {
        await axios.post(`${API_BASE}/api/knowledge/upload`, uploadPayload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            timeout: 600000 
        });
        successCount++;
      } catch (error) { 
          console.error(`Gagal: ${file.name}`, error);
          currentFailures.push(file.name);
      }
    }
    
    setUploading(false); 
    setFailedFiles(currentFailures);

    if (currentFailures.length === 0) {
        setUploadStatus({ type: 'success', message: `✅ Selesai! ${successCount} file berhasil diupload.` });
        setSelectedFiles([]); 
    } else {
        setUploadStatus({ 
            type: 'error', 
            message: `⚠️ Selesai. ${successCount} sukses, ${currentFailures.length} gagal.` 
        });
    }
    fetchUploadedFiles();
  };

  const handleSave = async () => {
    if (selectedFiles.length > 0) await handleUploadFiles();
    try {
        await updateAgentById(agentId, formData);
        if (selectedFiles.length === 0) alert("Data agent berhasil disimpan ✅");
    } catch(err) { alert("Gagal menyimpan data agent ❌"); }
    refreshAgents();
  };

  if (loading) return <div className="flex justify-center h-screen items-center text-gray-500 font-medium">Loading Agent...</div>;

  return (
    <div className="bg-gray-50 min-h-screen py-10 px-6 flex justify-center">
      <div className="max-w-5xl w-full bg-white p-10 rounded-2xl shadow-xl border border-gray-100">
        
        {/* HEADER */}
        <div className="flex justify-between items-start mb-8 border-b pb-6 border-gray-100">
            <div>
                <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Configure Agent</h1>
                <p className="text-gray-500 mt-2">Sesuaikan kepribadian dan knowledge base agent Anda.</p>
            </div>
            <span className="bg-indigo-50 text-indigo-600 px-4 py-2 rounded-full text-sm font-semibold border border-indigo-100">
                {formData.agentName || "New Agent"}
            </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          
          {/* LEFT COLUMN: IDENTITY */}
          <div className="flex flex-col gap-6">
            <div>
                <label className="text-sm font-bold text-gray-700 mb-2 block">Agent Name</label>
                <input type="text" name="agentName" value={formData.agentName} onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition shadow-sm" placeholder="e.g. HR Assistant" />
            </div>

            <div>
                <label className="text-sm font-bold text-gray-700 mb-2 block">Personality</label>
                <div className="grid grid-cols-2 gap-3 mb-2">
                    {PERSONALITY_OPTS.map((opt) => (
                        <button key={opt.label} onClick={() => setFormData({...formData, agentPersonality: opt.label})}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border text-center
                        ${formData.agentPersonality === opt.label 
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-100" 
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"}`}>
                        {opt.label}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-gray-400 italic">
                    ℹ️ {PERSONALITY_OPTS.find(p => p.label === formData.agentPersonality)?.desc}
                </p>
            </div>

            <div>
                <label className="text-sm font-bold text-gray-700 mb-2 block">System Prompt</label>
                <textarea name="agentPrompt" value={formData.agentPrompt} onChange={handleChange} rows={5}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 transition shadow-sm text-sm" 
                    placeholder="Instruksi spesifik untuk agent..." />
            </div>
          </div>

          {/* RIGHT COLUMN: KNOWLEDGE BASE */}
          <div className="flex flex-col gap-6">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 h-full flex flex-col">
                <label className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <CloudArrowUpIcon className="w-5 h-5 text-indigo-600" /> Knowledge Base Upload
                </label>
                
                {/* DRAG & DROP ZONE */}
                <div 
                    className={`relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all duration-300 cursor-pointer group bg-white flex-grow
                    ${isDragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 hover:border-indigo-400 hover:shadow-md"}
                    ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                    onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                    onClick={() => !uploading && fileInputRef.current.click()}
                >
                    <input type="file" ref={fileInputRef} className="hidden" multiple accept=".pdf,.txt,.xlsx,.xls" onChange={onFileSelect} />
                    <div className="p-4 bg-indigo-100 rounded-full mb-3 group-hover:scale-110 transition duration-300">
                        <DocumentArrowUpIcon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition">Klik atau Drop file di sini</p>
                    <p className="text-xs text-gray-400 mt-1">PDF & TXT (Max 50MB)</p>
                </div>

                {/* QUEUE & STATUS */}
                {(selectedFiles.length > 0 || uploadStatus.message) && (
                    <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm animate-fadeIn">
                        {uploadStatus.message ? (
                            <div className={`text-sm font-medium flex items-center gap-2 
                                ${uploadStatus.type === 'error' ? 'text-red-600' : 'text-indigo-600'}`}>
                                {uploadStatus.type === 'success' ? <CheckCircleIcon className="w-5 h-5"/> : <ArrowPathIcon className="w-5 h-5 animate-spin"/>}
                                {uploadStatus.message}
                            </div>
                        ) : (
                            <div className="text-sm font-medium text-gray-600 flex items-center gap-2">
                                <DocumentTextIcon className="w-5 h-5"/> {selectedFiles.length} file siap diupload
                            </div>
                        )}
                    </div>
                )}
            </div>
          </div>
        </div>

        {/* --- DOKUMEN TABLE (SCROLLABLE & STICKY HEADER) --- */}
        {uploadedHistory.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <CheckCircleIcon className="w-6 h-6 text-green-500" /> 
                    Uploaded Documents <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs ml-2">{uploadedHistory.length}</span>
                </h3>

                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    {/* 👇 INI KUNCINYA: max-h-[400px] biar scrollable */}
                    <div className="overflow-y-auto max-h-[400px]"> 
                        <table className="w-full text-left relative">
                            {/* 👇 INI KUNCINYA: sticky top-0 biar headernya diem */}
                            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-bold sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-6 py-4 bg-gray-50">Document Name</th>
                                    <th className="px-6 py-4 bg-gray-50">Size</th>
                                    <th className="px-6 py-4 bg-gray-50">Date</th>
                                    <th className="px-6 py-4 text-center bg-gray-50">Preview</th>
                                    <th className="px-6 py-4 text-right bg-gray-50">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {uploadedHistory.map((file) => (
                                    <tr key={file.id} className="hover:bg-indigo-50/30 transition duration-150">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-red-50 rounded-lg text-red-500">
                                                    <DocumentTextIcon className="w-5 h-5" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-700 truncate max-w-xs">{file.fileName}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                                                {file.file_size || "-"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {new Date(file.uploadedAt).toLocaleDateString('id-ID')}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {file.file_path ? (
                                                <a href={`${API_BASE}${file.file_path}`} target="_blank" rel="noreferrer"
                                                    className="text-indigo-600 hover:text-indigo-800 font-medium text-sm inline-flex items-center gap-1 hover:underline">
                                                    <EyeIcon className="w-4 h-4"/> View
                                                </a>
                                            ) : <span className="text-xs text-gray-300">No Preview</span>}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => handleDeleteHistory(file.id)} 
                                                className="text-gray-400 hover:text-red-600 transition p-2 hover:bg-red-50 rounded-full">
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {/* FOOTER ACTION */}
        <div className="mt-10 flex justify-end gap-4">
            <button onClick={() => window.location.href = '/dashboard'}
                className="px-6 py-3 rounded-xl font-semibold text-gray-600 hover:bg-gray-100 transition">
                Cancel
            </button>
            <button onClick={handleSave} disabled={uploading} 
                className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg shadow-indigo-200 transform transition-all 
                ${uploading ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-1"}`}>
                {uploading ? "Sedang Memproses..." : "Save Changes"}
            </button>
        </div>

      </div>
    </div>
  );
}